import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Megaphone,
  Send,
  Beaker,
  Trash2,
  Pencil,
  Clock,
  Users,
  MousePointerClick,
  Eye,
  Loader2,
  Plus,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * AnnouncementsTab — admin compose + list view for Bud Link broadcasts.
 * Every announcement fires both channels (in-app banner + push).
 */
const isoToInputLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const tzOffMs = d.getTimezoneOffset() * 60_000;
    return new Date(d.getTime() - tzOffMs).toISOString().slice(0, 16);
  } catch {
    return "";
  }
};
const inputLocalToIso = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const emptyForm = () => ({
  title: "",
  body: "",
  cta_url: "",
  cta_label: "",
  target_zip: "",
  target_radius_mi: 25,
  send_at: "",
  expires_at: "",
  active: true,
});

export const AnnouncementsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    api
      .get("/admin/announcements")
      .then((r) => setItems(r.data?.results || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    refresh();
  }, []);

  const openNew = () => {
    setForm(emptyForm());
    setEditing("new");
  };
  const openEdit = (ann) => {
    setForm({
      title: ann.title || "",
      body: ann.body || "",
      cta_url: ann.cta_url || "",
      cta_label: ann.cta_label || "",
      target_zip: ann.target_zip || "",
      target_radius_mi: ann.target_radius_mi || 25,
      send_at: isoToInputLocal(ann.send_at),
      expires_at: isoToInputLocal(ann.expires_at),
      active: ann.active ?? true,
    });
    setEditing(ann.id);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title + body are required");
      return;
    }
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      cta_url: form.cta_url.trim() || null,
      cta_label: form.cta_label.trim() || null,
      target_zip: form.target_zip.trim() || null,
      target_radius_mi: Number(form.target_radius_mi) || 25,
      send_at: inputLocalToIso(form.send_at),
      expires_at: inputLocalToIso(form.expires_at),
      active: !!form.active,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/announcements", payload);
        toast.success("Announcement created");
      } else {
        await api.put(`/admin/announcements/${editing}`, payload);
        toast.success("Announcement updated");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (ann) => {
    if (!window.confirm(`Delete "${ann.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${ann.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const sendNow = async (ann) => {
    if (!window.confirm(`Send "${ann.title}" to ALL push subscribers now?`)) return;
    try {
      const { data } = await api.post(`/admin/announcements/${ann.id}/send-now`);
      toast.success(`Sent · ${data?.push_sent ?? 0} push devices reached`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const testSend = async (ann) => {
    try {
      const { data } = await api.post(`/admin/announcements/${ann.id}/test-send`);
      toast.success(`Test sent · ${data?.admin_push_sent ?? 0} admin device(s)`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow mb-1">Announcements</div>
          <h2 className="font-serif text-2xl">Broadcast to every user</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg leading-relaxed">
            Write once — hits both the in-app banner (everyone) and Web
            Push (opt-in subscribers). Target by ZIP, schedule, or send
            now.
          </p>
        </div>
        {editing !== "new" && (
          <Button
            onClick={openNew}
            data-testid="announcement-new"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-5"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New announcement
          </Button>
        )}
      </div>

      {editing !== null && (
        <form
          onSubmit={save}
          data-testid="announcement-form"
          className="border border-border rounded-2xl bg-background/50 p-5 space-y-4 mb-6"
        >
          <div>
            <Label className="text-xs uppercase tracking-widest text-foreground/60">
              Title *
            </Label>
            <Input
              data-testid="announcement-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={120}
              placeholder="Fourth of July sale — 30% off flower storewide"
              className="h-11 mt-1.5"
              required
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-foreground/60">
              Body *
            </Label>
            <Textarea
              data-testid="announcement-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              maxLength={800}
              rows={4}
              placeholder="What you want everyone to know…"
              className="mt-1.5"
              required
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">
                CTA button URL (optional)
              </Label>
              <Input
                value={form.cta_url}
                onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                placeholder="/dispensaries/trulieve-reading"
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">
                CTA button label
              </Label>
              <Input
                value={form.cta_label}
                onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                maxLength={40}
                placeholder="See the sale"
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">
                Target ZIP (optional)
              </Label>
              <Input
                data-testid="announcement-zip"
                value={form.target_zip}
                onChange={(e) => setForm({ ...form, target_zip: e.target.value })}
                placeholder="19611"
                maxLength={5}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">
                Radius (miles)
              </Label>
              <Input
                type="number"
                min={5}
                max={250}
                value={form.target_radius_mi}
                onChange={(e) => setForm({ ...form, target_radius_mi: e.target.value })}
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">
                Schedule for (optional)
              </Label>
              <Input
                type="datetime-local"
                data-testid="announcement-send-at"
                value={form.send_at}
                onChange={(e) => setForm({ ...form, send_at: e.target.value })}
                className="h-11 mt-1.5"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Leave blank to send manually with the &quot;Send now&quot; button.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">
                Expires at (optional)
              </Label>
              <Input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className="h-11 mt-1.5"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Banner auto-hides after this time.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })}
            />
            <Label className="text-sm">Active (uncheck to save as draft)</Label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditing(null)}
              className="rounded-full h-10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="announcement-save"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
            >
              {busy ? "Saving…" : (editing === "new" ? "Create" : "Save changes")}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-foreground/50">Loading announcements…</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center">
          <Megaphone className="w-6 h-6 mx-auto mb-3 text-[#d4a268]" />
          <div className="font-serif text-xl mb-2">No announcements yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Compose one above to blast a message to every visitor + push
            notification.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((ann) => (
            <AnnRow
              key={ann.id}
              ann={ann}
              onEdit={() => openEdit(ann)}
              onDelete={() => del(ann)}
              onSendNow={() => sendNow(ann)}
              onTestSend={() => testSend(ann)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AnnRow = ({ ann, onEdit, onDelete, onSendNow, onTestSend }) => {
  const now = Date.now();
  const scheduled = ann.send_at ? new Date(ann.send_at).getTime() : null;
  const expires = ann.expires_at ? new Date(ann.expires_at).getTime() : null;
  const expired = expires && expires < now;
  const pending = scheduled && scheduled > now && !ann.push_sent_at;
  const sent = !!ann.push_sent_at;

  return (
    <div
      data-testid={`announcement-row-${ann.id}`}
      className="border border-border bg-card rounded-2xl p-5 flex items-start gap-4 flex-wrap"
    >
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-wider mb-1">
          {!ann.active ? (
            <span className="pill bg-secondary text-foreground/60">Paused</span>
          ) : expired ? (
            <span className="pill bg-amber-500/15 text-amber-300 border-amber-500/30">
              Expired
            </span>
          ) : pending ? (
            <span className="pill bg-sky-500/15 text-sky-300 border-sky-500/30">
              Scheduled
            </span>
          ) : sent ? (
            <span className="pill bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
              Sent
            </span>
          ) : (
            <span className="pill bg-secondary text-foreground/70">Live · not pushed</span>
          )}
          {ann.target_zip && (
            <span className="text-foreground/50 normal-case tracking-normal">
              🎯 {ann.target_zip} · {ann.target_radius_mi} mi
            </span>
          )}
          {scheduled && !sent && (
            <span className="text-foreground/50 normal-case tracking-normal flex items-center gap-1">
              <Clock className="w-3 h-3" />{" "}
              {new Date(scheduled).toLocaleString()}
            </span>
          )}
        </div>
        <div className="font-serif text-lg leading-tight">{ann.title}</div>
        <p className="text-sm text-foreground/70 mt-1 line-clamp-2">{ann.body}</p>
        <div className="flex gap-4 mt-2 text-[11px] text-foreground/55">
          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {ann.impressions || 0}</span>
          <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {ann.clicks || 0}</span>
          {ann.push_sent_at && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" /> Pushed {new Date(ann.push_sent_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap ml-auto">
        <Button
          size="sm"
          variant="outline"
          onClick={onTestSend}
          data-testid={`ann-test-${ann.id}`}
          className="rounded-full h-9 whitespace-normal"
        >
          <Beaker className="w-3.5 h-3.5 mr-1.5" /> Test to admin
        </Button>
        <Button
          size="sm"
          onClick={onSendNow}
          disabled={!ann.active}
          data-testid={`ann-send-${ann.id}`}
          className="rounded-full h-9 bg-primary text-primary-foreground hover:bg-primary/90 whitespace-normal"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" /> Send now
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onEdit}
          data-testid={`ann-edit-${ann.id}`}
          className="rounded-full h-9"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onDelete}
          data-testid={`ann-delete-${ann.id}`}
          className="rounded-full h-9 text-destructive hover:text-destructive"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default AnnouncementsTab;
